import { PDFFont, PDFPage, rgb } from 'pdf-lib';

/**
 * Trial-only "Scope" text placement on the E-Series agreement template.
 *
 * The new Trial Agreement document has no dedicated Scope section, so the
 * free-text scope captured in the trial form is rendered in the open space on
 * page 0, just below the cost/term table.
 */
export const TRIAL_SCOPE_PLACEMENT = {
  pageIndex: 0,
  x: 46,
  headingY: 156,
  bodyStartY: 140,
  fontSize: 9,
  lineHeight: 12,
  maxWidth: 520,
  minY: 46,
};

/** Replace smart punctuation with ASCII so Helvetica can render it. */
function clean(text: string): string {
  return text
    .replace(/[\u2018\u2019\u02BC]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\u2026/g, '...')
    .replace(/\u00A0/g, ' ');
}

/** Word-wrap text to a target width (pt), honoring explicit line breaks. */
function wrapText(text: string, font: PDFFont, size: number, maxWidth: number): string[] {
  const lines: string[] = [];
  for (const rawLine of clean(text).split(/\r?\n/)) {
    const words = rawLine.split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      lines.push('');
      continue;
    }
    let current = '';
    for (const word of words) {
      const candidate = current ? current + ' ' + word : word;
      if (font.widthOfTextAtSize(candidate, size) > maxWidth && current) {
        lines.push(current);
        current = word;
      } else {
        current = candidate;
      }
    }
    if (current) lines.push(current);
  }
  return lines;
}

/** Draw the trial Scope heading + wrapped body text onto the given page. */
export function drawTrialAgreementScopeText(page: PDFPage, scope: string, font: PDFFont): void {
  const text = (scope ?? '').trim();
  if (!text) return;
  const p = TRIAL_SCOPE_PLACEMENT;
  page.drawText('Scope:', { x: p.x, y: p.headingY, size: 11, font, color: rgb(0, 0, 0) });
  let y = p.bodyStartY;
  for (const line of wrapText(text, font, p.fontSize, p.maxWidth)) {
    if (y < p.minY) break;
    if (line) page.drawText(line, { x: p.x, y, size: p.fontSize, font, color: rgb(0, 0, 0) });
    y -= p.lineHeight;
  }
}
